import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { actionApi, UndoActionOptions, UndoActionResponse } from './action-api';
import { 
  ActionFilter, 
  PaginationParams, 
  EnhancedActionLog, 
  PaginatedResponse,
  GroupedActionsResponse
} from '@/types/action-types';

/**
 * Configuration for action query caching
 */
const ACTION_QUERY_CONFIG = {
  staleTime: 1000 * 60 * 2, // 2 minutes cache
  refetchOnWindowFocus: false,
  refetchOnMount: true,
};

/**
 * Hook for fetching actions for a specific campaign
 *
 * @param campaignId - Campaign ID to fetch actions for
 * @param filters - Optional filters for actions
 * @param pagination - Optional pagination parameters
 * @param options - Additional query options
 */
export const useActions = (
  campaignId: number,
  filters: ActionFilter = {},
  pagination: PaginationParams = { skip: 0, limit: 10 },
  options?: Omit<UseQueryOptions<PaginatedResponse<EnhancedActionLog>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PaginatedResponse<EnhancedActionLog>>({
    queryKey: ['campaign-actions', campaignId, filters, pagination],
    queryFn: () => actionApi.getCampaignActions(campaignId, { ...filters, ...pagination }),
    ...ACTION_QUERY_CONFIG,
    ...options,
    enabled: options?.enabled !== false && !!campaignId && campaignId > 0,
  });
};

/**
 * Hook for fetching actions across all organizations a user belongs to
 *
 * @param filters - Optional filters for actions
 * @param pagination - Optional pagination parameters
 * @param options - Additional query options
 */
export const useAllOrganizationsActions = (
  filters: ActionFilter = {},
  pagination: PaginationParams = { skip: 0, limit: 10 },
  options?: Omit<UseQueryOptions<PaginatedResponse<EnhancedActionLog>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PaginatedResponse<EnhancedActionLog>>({
    queryKey: ['all-organizations-actions', filters, pagination],
    queryFn: () => actionApi.getAllActions({ ...filters, ...pagination }),
    ...ACTION_QUERY_CONFIG,
    ...options,
  });
};

/**
 * Hook for campaign overview page with limited actions
 *
 * @param campaignId - Campaign ID to fetch actions for
 * @param options - Additional query options
 */
export const useCampaignOverviewActions = (
  campaignId: number,
  options?: Omit<UseQueryOptions<PaginatedResponse<EnhancedActionLog>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PaginatedResponse<EnhancedActionLog>>({
    queryKey: ['campaign-overview-actions', campaignId],
    queryFn: () => actionApi.getCampaignActions(campaignId, { skip: 0, limit: 10 }),
    ...ACTION_QUERY_CONFIG,
    staleTime: 1000 * 60 * 5, // 5 minutes cache for overview
    ...options,
    enabled: options?.enabled !== false && !!campaignId && campaignId > 0,
  });
};

/**
 * Hook for fetching details of a specific action
 *
 * @param actionId - Action ID to fetch details for
 * @param options - Additional query options
 */
export const useActionDetails = (
  actionId: number | null,
  options?: Omit<UseQueryOptions<EnhancedActionLog | null>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<EnhancedActionLog | null>({
    queryKey: ['action-details', actionId],
    queryFn: () => (actionId ? actionApi.getActionDetails(actionId) : Promise.resolve(null)),
    ...ACTION_QUERY_CONFIG,
    ...options,
    enabled: options?.enabled !== false && !!actionId,
  });
};

/**
 * Hook for fetching actions related to specific recommendations
 *
 * @param recommendationIds - Recommendation IDs to fetch related actions for
 * @param campaignId - Campaign ID context
 * @param options - Additional query options
 */
export const useRelatedActions = (
  recommendationIds: number[],
  campaignId: number,
  options?: Omit<UseQueryOptions<EnhancedActionLog[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<EnhancedActionLog[]>({
    queryKey: ['related-actions', recommendationIds, campaignId],
    queryFn: () => actionApi.getActionsByRecommendations(recommendationIds, campaignId),
    ...ACTION_QUERY_CONFIG,
    ...options,
    enabled: options?.enabled !== false && recommendationIds.length > 0 && !!campaignId,
  });
};

/**
 * Hook for fetching actions for a specific entity
 *
 * @param entityType - Type of entity to fetch actions for
 * @param entityId - Entity ID to fetch actions for
 * @param filters - Optional filters for actions
 * @param pagination - Optional pagination parameters
 * @param options - Additional query options
 */
export const useEntityActions = (
  entityType: string,
  entityId: number,
  filters: ActionFilter = {},
  pagination: PaginationParams = { skip: 0, limit: 10 },
  options?: Omit<UseQueryOptions<PaginatedResponse<EnhancedActionLog>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PaginatedResponse<EnhancedActionLog>>({
    queryKey: ['entity-actions', entityType, entityId, filters, pagination],
    queryFn: () => actionApi.getActionsByEntity(entityType, entityId, { ...filters, ...pagination }),
    ...ACTION_QUERY_CONFIG,
    ...options,
    enabled: options?.enabled !== false && !!entityId && !!entityType,
  });
};

/**
 * Type for undo action mutation parameters
 */
export interface UndoActionParams {
  actionId: number;
  options?: {
    params?: UndoActionOptions;
  };
}

/**
 * Hook for undoing an action (mutation)
 */
export const useUndoActionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UndoActionResponse, Error, UndoActionParams>({
    mutationFn: ({ actionId, options }) => actionApi.undoAction(actionId, options),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['campaign-actions'] });
      queryClient.invalidateQueries({ queryKey: ['all-organizations-actions'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-overview-actions'] });
      queryClient.invalidateQueries({ queryKey: ['action-details'] });
      queryClient.invalidateQueries({ queryKey: ['related-actions'] });
      queryClient.invalidateQueries({ queryKey: ['entity-actions'] });
    },
  });
};

/**
 * Legacy hook for undoing an action - maintains backward compatibility
 * while the codebase is migrated to the new mutation pattern
 */
export const useUndoAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actionId: number) => {
      return actionApi.undoAction(actionId);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['campaign-actions'] });
      queryClient.invalidateQueries({ queryKey: ['all-organizations-actions'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-overview-actions'] });
      queryClient.invalidateQueries({ queryKey: ['action-details'] });
      queryClient.invalidateQueries({ queryKey: ['related-actions'] });
      queryClient.invalidateQueries({ queryKey: ['entity-actions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-actions'] });
    },
  });
};

/**
 * Hook for fetching grouped actions with consistent keyword counting
 * 
 * This is used by the two-column-tasks-inbox-view to show a consistent
 * count between left and right columns.
 * 
 * @param params - Object containing filters, pagination, and campaignId
 * @param params.filters - Optional filters for actions
 * @param params.pagination - Optional pagination parameters
 * @param params.campaignId - Optional campaign ID to filter by
 * @param params.options - Additional query options
 */
export function useGroupedActionsQuery({
  filters,
  pagination,
  campaignId,
  options,
}: {
  filters?: ActionFilter;
  pagination?: PaginationParams;
  campaignId?: number;
  options?: Omit<UseQueryOptions<GroupedActionsResponse>, 'queryKey' | 'queryFn'>;
} = {}) {
  const queryKey = campaignId 
    ? ['grouped-actions', campaignId, filters]
    : ['grouped-actions', 'all', filters];
  
  return useQuery<GroupedActionsResponse>({
    queryKey,
    queryFn: async () => {
      const params = {
        ...(filters || {}),
        ...(pagination || {}),
        ...(campaignId ? { campaign_id: campaignId } : {}),
      };
      return actionApi.getGroupedActions(params);
    },
    ...ACTION_QUERY_CONFIG,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    ...options,
  });
}

/**
 * Hook for campaign overview page with grouped actions
 * 
 * @param params - Object containing campaignId and options
 * @param params.campaignId - Campaign ID to fetch grouped actions for
 * @param params.options - Additional query options
 */
export function useCampaignGroupedActionsQuery({
  campaignId,
  options,
}: {
  campaignId: number;
  options?: Omit<UseQueryOptions<GroupedActionsResponse>, 'queryKey' | 'queryFn'>;
}) {
  return useQuery<GroupedActionsResponse>({
    queryKey: ['grouped-actions', campaignId],
    queryFn: async () => {
      return actionApi.getGroupedActions({ 
        skip: 0, 
        limit: 10,
        campaign_id: campaignId
      });
    },
    ...ACTION_QUERY_CONFIG,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    ...options,
    enabled: options?.enabled !== false && !!campaignId && campaignId > 0,
  });
}
